// Path: ./src/utils/localStorage.ts
let localStorageAvailable: boolean | null = null;
let sessionStorageAvailable: boolean | null = null;

const getLocalStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const testStorage = (getStorage: () => Storage | null): boolean => {
  try {
    const storage = getStorage();
    if (!storage) {
      return false;
    }

    const testKey = "__storage_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const isLocalStorageAvailable = (): boolean => {
  if (localStorageAvailable !== null) {
    return localStorageAvailable;
  }

  localStorageAvailable = testStorage(getLocalStorage);
  return localStorageAvailable;
};

const isSessionStorageAvailable = (): boolean => {
  if (sessionStorageAvailable !== null) {
    return sessionStorageAvailable;
  }

  sessionStorageAvailable = testStorage(getSessionStorage);
  return sessionStorageAvailable;
};

const safeLocalStorageGetItem = (key: string): string | null => {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    return getLocalStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeLocalStorageSetItem = (key: string, value: string): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    getLocalStorage()?.setItem(key, value);
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeLocalStorageRemoveItem = (key: string): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    getLocalStorage()?.removeItem(key);
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeLocalStorageClear = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    getLocalStorage()?.clear();
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeLocalStorageHasItem = (key: string): boolean =>
  safeLocalStorageGetItem(key) !== null;

const safeSessionStorageGetItem = (key: string): string | null => {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  try {
    return getSessionStorage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const safeSessionStorageSetItem = (key: string, value: string): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    getSessionStorage()?.setItem(key, value);
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeSessionStorageRemoveItem = (key: string): void => {
  if (!isSessionStorageAvailable()) {
    return;
  }

  try {
    getSessionStorage()?.removeItem(key);
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeSessionStorageHasItem = (key: string): boolean =>
  safeSessionStorageGetItem(key) !== null;

const localStorageSet = (key: string, value: string | unknown): void => {
  if (typeof value !== "string") {
    safeLocalStorageSetJSON(key, value);
    return;
  }

  safeLocalStorageSetItem(key, value);
};

const localStorageGet = (key: string): unknown => {
  return safeLocalStorageGetJSON(key, null);
};

const safeLocalStorageGetJSON = <T>(key: string, defaultValue: T): T => {
  const raw = safeLocalStorageGetItem(key);
  if (raw === null) {
    return defaultValue;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
};

const safeLocalStorageSetJSON = <T>(key: string, value: T): void => {
  try {
    safeLocalStorageSetItem(key, JSON.stringify(value));
  } catch {
    // Safari private browsing / restricted contexts
  }
};

const safeSessionStorageGetJSON = <T>(key: string, defaultValue: T): T => {
  const raw = safeSessionStorageGetItem(key);
  if (raw === null) {
    return defaultValue;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
};

const safeSessionStorageSetJSON = <T>(key: string, value: T): void => {
  try {
    safeSessionStorageSetItem(key, JSON.stringify(value));
  } catch {
    // Safari private browsing / restricted contexts
  }
};

export {
  isLocalStorageAvailable,
  isSessionStorageAvailable,
  localStorageGet,
  localStorageSet,
  safeLocalStorageClear,
  safeLocalStorageGetItem,
  safeLocalStorageGetJSON,
  safeLocalStorageHasItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
  safeLocalStorageSetJSON,
  safeSessionStorageGetItem,
  safeSessionStorageGetJSON,
  safeSessionStorageHasItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
  safeSessionStorageSetJSON,
};
